import React, { useState, useMemo, useEffect, useRef, useContext } from 'react';
import { Package, FileCode, User, ExternalLink, Activity, GitBranch, Network, TrendingUp, Search, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, ArrowUpDown, ArrowUp, ArrowDown, AlertTriangle, X, Wifi, Clock, Zap, Shield, Eye, Info, Target, Server, Layers } from 'lucide-react';
import api from '../utils/api';
import { AuthContext } from '../index';
import DetailsModal from './modals/DetailsModal';
import NetworkMapModal from './modals/NetworkMapModal';
import ConfidenceSankey from './visualizations/ConfidenceSankey';
import PlatformHealthComparison from './visualizations/PlatformHealthComparison';
import StateTransitionSwimlane from './visualizations/StateTransitionSwimlane';
import EntityTypeSunburst from './visualizations/EntityTypeSunburst';
import DependencyNetworkGraph from './visualizations/DependencyNetworkGraph';
import HealthScoreViolin from './visualizations/HealthScoreViolin';
import TimeToRecoveryChart from './visualizations/TimeToRecoveryChart';
import ConfidenceHealthCorrelation from './visualizations/ConfidenceHealthCorrelation';

const Software = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [loading, setLoading] = useState(true);
  const [allSoftwareData, setAllSoftwareData] = useState([]);
  const [unhealthyDevices, setUnhealthyDevices] = useState([]);
  const [showTroubleshootBox, setShowTroubleshootBox] = useState(false);
  const [troubleshootStream, setTroubleshootStream] = useState('');
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('table'); // 'table', 'health-timeline', 'network-graph', 'risk-matrix'
  const [selectedItem, setSelectedItem] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showNetworkModal, setShowNetworkModal] = useState(false);
  const [healthStats, setHealthStats] = useState({
    healthy: 0,
    degraded: 0,
    unhealthy: 0,
    unknown: 0,
    avgScore: 0
  });
  const [isAdmin, setIsAdmin] = useState(false);
  const troubleshootWsRef = useRef(null);
  const streamContentRef = useRef(null);
  
  const { auth } = useContext(AuthContext);
  
  // Check admin status
  useEffect(() => {
    if (auth.isAuthenticated()) {
      auth.getUserInfo().then(info => {
        setIsAdmin(info.isAdmin || false);
      }).catch(() => {
        setIsAdmin(false);
      });
    }
  }, [auth]);

  // Convert to Mountain Time
  const convertToMountainTime = (dateString) => {
    if (!dateString) return { date: 'N/A', time: '' };
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return { date: 'N/A', time: '' };
      const dateStr = date.toLocaleString('en-US', {
        timeZone: 'America/Denver',
        month: '2-digit',
        day: '2-digit',
        year: 'numeric'
      });
      const timeStr = date.toLocaleString('en-US', {
        timeZone: 'America/Denver',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
      return { date: dateStr, time: timeStr };
    } catch (error) {
      return { date: 'N/A', time: '' };
    }
  };

  // Fetch data from new API endpoint
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        console.log('[Software] Fetching data from UI changes API...');
        const response = await api.getUIChanges();
        const allHealthData = response.items || response.data || response || [];
        
        console.log(`[Software] Loaded ${allHealthData.length} records`);

        if (!Array.isArray(allHealthData) || allHealthData.length === 0) {
          console.warn('[Software] No data received from API');
          setError('No data available.');
          setAllSoftwareData([]);
          setUnhealthyDevices([]);
          setLoading(false);
          return;
        }

        // Calculate health statistics
        let healthy = 0, degraded = 0, unhealthy = 0, unknown = 0;
        let totalScore = 0, scoreCount = 0;

        // Transform data
        const transformedData = allHealthData.map((item, index) => {
          const rels = item.relationships || {};
          const upstream = rels.upstream || [];
          const downstream = rels.downstream || [];
          const totalRels = parseInt(rels.total_relationships) || (upstream.length + downstream.length);

          let healthStatus = 'UNKNOWN';
          let healthScore = 0;

          if (item.main_entity_health && item.main_entity_health.status) {
            healthStatus = item.main_entity_health.status;
            healthScore = parseInt(item.main_entity_health.score) || 0;
            
            // Count health statuses
            if (healthStatus === 'HEALTHY') healthy++;
            else if (healthStatus === 'DEGRADED') degraded++;
            else if (healthStatus === 'UNHEALTHY') unhealthy++;
            else unknown++;
            
            if (healthScore > 0) {
              totalScore += healthScore;
              scoreCount++;
            }
          } else {
            unknown++;
          }

          const dateTime = convertToMountainTime(item.latest_state_start || item.requested_by_date || item.created_at);
          const description = item.short_description || item.u_app_name || item.u_version || item.description || 'Unknown Service';
          const entityType = item.step2_entity_type || item.entity_type || 'SERVICE';
          
          const dynatraceLink = item.step2_dynatrace_link || item.dynatrace_link || null;
          let servicenowLink = item.servicenow_link || null;
          if (!servicenowLink && item.sys_id) {
            servicenowLink = `https://dish.service-now.com/nav_to.do?uri=change_request.do?sys_id=${item.sys_id}`;
          }

          let entity = 'package';
          if (entityType.includes('SERVICE') || entityType.includes('APPLICATION')) entity = 'package';
          else if (entityType.includes('FILE') || entityType.includes('DATABASE')) entity = 'file';
          else entity = 'user';

          let stateColor = 'text-gray-400';
          if (item.current_state === 'Closed Complete') stateColor = 'text-lime-accent';
          else if (item.current_state === 'Move to Prod') stateColor = 'text-cyan-accent';
          else if (item.current_state === 'In Progress' || item.current_state === 'Prod Verification') stateColor = 'text-yellow-accent';

          return {
            id: index + 1,
            date: dateTime.date,
            time: dateTime.time,
            entity: entity,
            entityType: entityType,
            changeNumber: item.change_number || item.id || 'N/A',
            description: description,
            author: `by ${item.opened_by || 'System'}`,
            state: item.current_state || 'Unknown',
            stateColor: stateColor,
            health: healthStatus,
            healthScore: healthScore,
            dynatrace: !!dynatraceLink,
            dynatraceLink: dynatraceLink,
            servicenowLink: servicenowLink,
            dependencies: totalRels > 0 ? `${totalRels} dependenc${totalRels === 1 ? 'y' : 'ies'}` : 'No dependencies',
            totalRelationships: totalRels,
            confidenceScore: item.step2_confidence_score || 0,
            healthCheckCount: item.health_check_count || 0,
            healthCheckHistory: item.health_check_history || [],
            raw_data: item
          };
        });

        console.log(`[Software] Transformed ${transformedData.length} items`);
        setAllSoftwareData(transformedData);

        // Set health statistics
        setHealthStats({
          healthy,
          degraded,
          unhealthy,
          unknown,
          avgScore: scoreCount > 0 ? Math.round(totalScore / scoreCount) : 0
        });

        // Filter unhealthy items for compact display (max 5)
        const unhealthyItems = transformedData
          .filter(item => item.health === 'DEGRADED' || item.health === 'UNHEALTHY')
          .slice(0, 5)
          .map((item, idx) => ({
            id: idx + 1,
            changeNumber: item.changeNumber,
            description: item.description,
            health: item.health,
            healthScore: item.healthScore,
            severity: item.health === 'UNHEALTHY' ? 'critical' : 'warning',
            timestamp: `${item.date} ${item.time}`
          }));
        console.log(`[Software] Found ${unhealthyItems.length} unhealthy items`);
        setUnhealthyDevices(unhealthyItems);

      } catch (error) {
        console.error('[Software] Error loading software data:', error);
        setError(error.message || 'Failed to load data.');
        setAllSoftwareData([]);
        setUnhealthyDevices([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const dismissDevice = (id) => {
    setUnhealthyDevices(prev => prev.filter(device => device.id !== id));
  };

  // Troubleshoot WebSocket functionality
  const addToStream = (text) => {
    setTroubleshootStream(prev => {
      const newText = prev ? prev + '\n' + text : text;
      setTimeout(() => {
        if (streamContentRef.current) {
          streamContentRef.current.scrollTop = streamContentRef.current.scrollHeight;
        }
      }, 0);
      return newText;
    });
  };

  const startTroubleshoot = async () => {
    try {
      setShowTroubleshootBox(true);
      setTroubleshootStream('');
      addToStream('⏳ Connecting to troubleshooting service...');

      const WS_URL = 'wss://1r5x85vspl.execute-api.us-west-2.amazonaws.com/production';
      const ws = new WebSocket(WS_URL);

      ws.onopen = () => {
        console.log('[Troubleshoot] Connected');
        addToStream('🔌 Connected! Starting scan...');
        ws.send(JSON.stringify({ action: 'troubleshoot' }));
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          let message = '';
          switch(data.type) {
            case 'analysis_start':
              message = '🚀 ' + (data.message || 'Starting analysis...');
              break;
            case 'item_start':
              message = '\n📋 ' + (data.message || 'Starting item');
              break;
            case 'item_progress':
            case 'diagnostic_step':
            case 'data_collected':
            case 'metric_collection':
              message = '⏳ ' + (data.message || 'Processing...');
              break;
            case 'ai_thinking':
              message = '🤖 ' + (data.agent || 'AI') + ': ' + (data.message || 'Analyzing...');
              break;
            case 'reasoning_update':
              const reasoning = data.reasoning || data.full_reasoning?.output || '';
              message = '\n💭 ' + (data.agent || 'AI') + ':\n' + reasoning.substring(0, 200);
              break;
            case 'item_result':
              const changeNum = data.data?.change_number || 'Unknown';
              const status = data.data?.health_status || 'UNKNOWN';
              message = '\n✅ ' + changeNum + ' - ' + status;
              break;
            case 'complete':
              message = '\n\n✅ ' + (data.message || 'Scan complete!');
              break;
            case 'error':
              message = '\n❌ ERROR: ' + (data.message || 'Unknown error');
              break;
            default:
              if (data.message) message = data.message;
          }
          if (message) addToStream(message);
        } catch (error) {
          console.error('[Troubleshoot] Parse error:', error);
        }
      };

      ws.onerror = (error) => {
        console.error('[Troubleshoot] Error:', error);
        addToStream('\n❌ Connection error occurred');
      };

      ws.onclose = () => {
        console.log('[Troubleshoot] Connection closed');
      };

      troubleshootWsRef.current = ws;
    } catch (error) {
      console.error('[Troubleshoot] Error:', error);
      addToStream('\n❌ Connection failed: ' + error.message);
    }
  };

  const closeTroubleshootBox = () => {
    if (troubleshootWsRef.current) {
      troubleshootWsRef.current.close();
      troubleshootWsRef.current = null;
    }
    setShowTroubleshootBox(false);
    setTroubleshootStream('');
  };

  useEffect(() => {
    return () => {
      if (troubleshootWsRef.current) {
        troubleshootWsRef.current.close();
      }
    };
  }, []);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) return <ArrowUpDown size={14} className="opacity-50" />;
    return sortConfig.direction === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />;
  };

  const filteredAndSortedData = useMemo(() => {
    let filtered = allSoftwareData.filter(item =>
      item.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.changeNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.author.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.state.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (sortConfig.key) {
      filtered.sort((a, b) => {
        let aVal = a[sortConfig.key];
        let bVal = b[sortConfig.key];
        
        if (sortConfig.key === 'date') {
          const aDateStr = a.date + ' ' + a.time;
          const bDateStr = b.date + ' ' + b.time;
          const aDate = new Date(aDateStr);
          const bDate = new Date(bDateStr);
          aVal = isNaN(aDate.getTime()) ? 0 : aDate.getTime();
          bVal = isNaN(bDate.getTime()) ? 0 : bDate.getTime();
        }
        
        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return filtered;
  }, [searchTerm, sortConfig, allSoftwareData]);

  const totalPages = Math.ceil(filteredAndSortedData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedData = filteredAndSortedData.slice(startIndex, startIndex + itemsPerPage);

  const getEntityIcon = (entity) => {
    switch(entity) {
      case 'package': return <Package size={20} className="text-purple-accent" />;
      case 'file': return <FileCode size={20} className="text-pink-500" />;
      case 'user': return <User size={20} className="text-orange-accent" />;
      default: return <Package size={20} className="text-purple-accent" />;
    }
  };

  const getHealthIcon = (health) => {
    switch(health) {
      case 'HEALTHY':
        return <Activity size={16} className="text-green-500" />;
      case 'DEGRADED':
        return <Activity size={16} className="text-yellow-500" />;
      case 'UNHEALTHY':
        return <Activity size={16} className="text-red-500" />;
      case 'PENDING':
        return <Activity size={16} className="text-cyan-accent" />;
      default:
        return <Activity size={16} className="text-gray-500" />;
    }
  };

  const getHealthColor = (score) => {
    if (score >= 80) return '#10b981'; // green
    if (score >= 60) return '#eab308'; // yellow
    if (score >= 30) return '#f97316'; // orange
    return '#ef4444'; // red
  };

  // Health Timeline Heatmap Component
  const HealthTimelineHeatmap = () => {
    const heatmapData = filteredAndSortedData.slice(0, 20).map(item => {
      const history = item.healthCheckHistory || [];
      return {
        changeNumber: item.changeNumber,
        description: item.description.substring(0, 30),
        healthChecks: history.slice(-10).map(check => ({
          timestamp: check.timestamp,
          score: check.main_health?.score || 0,
          status: check.main_health?.status || 'UNKNOWN'
        }))
      };
    });

    return (
      <div className="bg-dark-card rounded-lg border border-dark-border p-6">
        <h3 className="text-xl font-bold text-cyan-accent mb-4">Health Timeline Heatmap</h3>
        <div className="space-y-2">
          {heatmapData.map((item, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <div className="w-32 text-xs text-gray-400 truncate" title={item.changeNumber}>
                {item.changeNumber}
              </div>
              <div className="flex-1 flex gap-1">
                {item.healthChecks.length > 0 ? (
                  item.healthChecks.map((check, checkIdx) => (
                    <div
                      key={checkIdx}
                      className="flex-1 h-8 rounded"
                      style={{
                        backgroundColor: getHealthColor(check.score),
                        boxShadow: `0 0 10px ${getHealthColor(check.score)}40`
                      }}
                      title={`Score: ${check.score} - ${check.status}`}
                    />
                  ))
                ) : (
                  <div className="flex-1 h-8 rounded bg-gray-700" title="No health data" />
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Deployment Risk Matrix Component
  const DeploymentRiskMatrix = () => {
    const riskData = filteredAndSortedData.map(item => ({
      changeNumber: item.changeNumber,
      confidenceScore: item.confidenceScore || 50,
      complexity: item.totalRelationships || 0,
      healthScore: item.healthScore || 0,
      health: item.health
    }));

    return (
      <div className="bg-dark-card rounded-lg border border-dark-border p-6">
        <h3 className="text-xl font-bold text-cyan-accent mb-4">Deployment Risk Matrix</h3>
        <div className="relative h-96 border border-dark-border rounded">
          {/* Quadrant labels */}
          <div className="absolute top-2 left-2 text-xs text-orange-accent">High Risk</div>
          <div className="absolute top-2 right-2 text-xs text-yellow-accent">Moderate Risk</div>
          <div className="absolute bottom-2 left-2 text-xs text-cyan-accent">Low Risk</div>
          <div className="absolute bottom-2 right-2 text-xs text-lime-accent">Safe</div>
          
          {/* Scatter plot */}
          <div className="relative w-full h-full p-8">
            {riskData.map((item, idx) => {
              const x = (item.confidenceScore / 100) * 100;
              const y = 100 - ((item.complexity / Math.max(...riskData.map(d => d.complexity), 1)) * 100);
              const size = Math.max(8, Math.min(20, item.healthScore / 5));
              
              return (
                <div
                  key={idx}
                  className="absolute rounded-full cursor-pointer transition-all hover:scale-150"
                  style={{
                    left: `${x}%`,
                    top: `${y}%`,
                    width: `${size}px`,
                    height: `${size}px`,
                    backgroundColor: getHealthColor(item.healthScore),
                    boxShadow: `0 0 15px ${getHealthColor(item.healthScore)}80`,
                    transform: 'translate(-50%, -50%)'
                  }}
                  title={`${item.changeNumber}\nConfidence: ${item.confidenceScore}%\nComplexity: ${item.complexity}\nHealth: ${item.healthScore}`}
                />
              );
            })}
          </div>
          
          {/* Axes */}
          <div className="absolute bottom-0 left-0 right-0 text-center text-xs text-gray-500 pb-1">
            Confidence Score →
          </div>
          <div className="absolute top-0 bottom-0 left-0 flex items-center text-xs text-gray-500 pl-1"
               style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>
            Complexity →
          </div>
        </div>
      </div>
    );
  };

  // Real-Time Health Pulse Dashboard
  const HealthPulseDashboard = () => {
    return (
      <div className="bg-dark-card rounded-lg border border-dark-border p-6">
        <h3 className="text-xl font-bold text-cyan-accent mb-4">Real-Time Health Pulse</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {filteredAndSortedData.slice(0, 12).map((item, idx) => (
            <div
              key={idx}
              className="relative p-4 rounded-lg border transition-all hover:scale-105 cursor-pointer"
              style={{
                backgroundColor: '#0d0d0d',
                borderColor: getHealthColor(item.healthScore),
                boxShadow: `0 0 20px ${getHealthColor(item.healthScore)}40`,
                animation: item.health === 'UNHEALTHY' ? 'pulse 2s infinite' : 'none'
              }}
            >
              <div className="text-xs text-gray-400 truncate mb-2" title={item.changeNumber}>
                {item.changeNumber}
              </div>
              <div className="text-2xl font-bold mb-2" style={{ color: getHealthColor(item.healthScore) }}>
                {item.healthScore}
              </div>
              <div className="text-xs text-gray-500">{item.health}</div>
              
              {/* Mini sparkline */}
              <div className="mt-2 flex gap-0.5 h-6 items-end">
                {(item.healthCheckHistory || []).slice(-8).map((check, i) => {
                  const score = check.main_health?.score || 0;
                  const height = (score / 100) * 100;
                  return (
                    <div
                      key={i}
                      className="flex-1 rounded-t"
                      style={{
                        height: `${height}%`,
                        backgroundColor: getHealthColor(score),
                        opacity: 0.7
                      }}
                    />
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="p-6 space-y-6 relative">
      {/* Troubleshooting Stream Box */}
      {showTroubleshootBox && (
        <div className="fixed top-20 right-5 w-96 max-h-[600px] bg-[rgba(15,15,15,0.98)] border border-red-500/50 rounded-xl shadow-2xl z-50 overflow-hidden"
             style={{ boxShadow: '0 8px 32px rgba(0, 0, 0, 0.8)' }}>
          <div className="p-3 bg-gradient-to-r from-red-600 to-red-700 flex justify-between items-center cursor-pointer"
               onClick={() => setShowTroubleshootBox(!showTroubleshootBox)}>
            <div className="flex items-center gap-2 text-white font-semibold">
              <Wifi size={16} />
              <span>AI Troubleshooting</span>
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                closeTroubleshootBox();
              }}
              className="text-white hover:text-red-200 transition-colors"
            >
              <X size={16} />
            </button>
          </div>
          <div
            ref={streamContentRef}
            className="p-4 max-h-[540px] overflow-y-auto font-mono text-xs leading-relaxed text-gray-300"
            style={{ fontFamily: "'SF Mono', 'Monaco', 'Inconsolata', 'Fira Code', monospace" }}
          >
            <span className="whitespace-pre-wrap">{troubleshootStream}</span>
            <span className="inline-block w-2 h-3.5 bg-red-500 ml-1 animate-pulse"></span>
          </div>
        </div>
      )}

      {/* Health Statistics Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-dark-card rounded-lg border border-lime-accent/40 p-4"
             style={{ boxShadow: '0 0 20px rgba(178, 255, 89, 0.2)' }}>
          <div className="flex items-center justify-between mb-2">
            <Shield size={20} className="text-lime-accent" />
            <span className="text-2xl font-bold text-lime-accent">{healthStats.healthy}</span>
          </div>
          <div className="text-xs text-gray-400">Healthy</div>
        </div>
        
        <div className="bg-dark-card rounded-lg border border-yellow-accent/40 p-4"
             style={{ boxShadow: '0 0 20px rgba(234, 179, 8, 0.2)' }}>
          <div className="flex items-center justify-between mb-2">
            <AlertTriangle size={20} className="text-yellow-accent" />
            <span className="text-2xl font-bold text-yellow-accent">{healthStats.degraded}</span>
          </div>
          <div className="text-xs text-gray-400">Degraded</div>
        </div>
        
        <div className="bg-dark-card rounded-lg border border-red-500/40 p-4"
             style={{ boxShadow: '0 0 20px rgba(239, 68, 68, 0.2)' }}>
          <div className="flex items-center justify-between mb-2">
            <X size={20} className="text-red-500" />
            <span className="text-2xl font-bold text-red-500">{healthStats.unhealthy}</span>
          </div>
          <div className="text-xs text-gray-400">Unhealthy</div>
        </div>
        
        <div className="bg-dark-card rounded-lg border border-gray-500/40 p-4"
             style={{ boxShadow: '0 0 20px rgba(107, 114, 128, 0.2)' }}>
          <div className="flex items-center justify-between mb-2">
            <Eye size={20} className="text-gray-500" />
            <span className="text-2xl font-bold text-gray-500">{healthStats.unknown}</span>
          </div>
          <div className="text-xs text-gray-400">Unknown</div>
        </div>
        
        <div className="bg-dark-card rounded-lg border border-cyan-accent/40 p-4"
             style={{ boxShadow: '0 0 20px rgba(0, 229, 255, 0.2)' }}>
          <div className="flex items-center justify-between mb-2">
            <Zap size={20} className="text-cyan-accent" />
            <span className="text-2xl font-bold text-cyan-accent">{healthStats.avgScore}</span>
          </div>
          <div className="text-xs text-gray-400">Avg Score</div>
        </div>
      </div>

      {/* Compact Unhealthy Devices List */}
      {unhealthyDevices.length > 0 && (
        <div className="bg-dark-card rounded-lg border border-orange-accent/40 p-4"
             style={{ boxShadow: '0 0 20px rgba(255, 107, 53, 0.2)' }}>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <AlertTriangle size={18} className="text-orange-accent animate-pulse" />
              <h3 className="text-sm font-bold text-orange-accent">
                {unhealthyDevices.length} Unhealthy Service{unhealthyDevices.length > 1 ? 's' : ''}
              </h3>
            </div>
          </div>
          
          <div className="space-y-2">
            {unhealthyDevices.map((device) => (
              <div key={device.id}
                   className="flex items-center justify-between p-2 rounded bg-dark-border/20 border border-dark-border/40 hover:border-orange-accent/30 transition-all group text-sm">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                    device.severity === 'critical' ? 'bg-red-500 animate-pulse' : 'bg-yellow-500'
                  }`} />
                  <span className="font-semibold text-white truncate">{device.changeNumber}</span>
                  <span className={`text-xs px-1.5 py-0.5 rounded flex-shrink-0 ${
                    device.severity === 'critical' 
                      ? 'bg-red-500/20 text-red-400' 
                      : 'bg-yellow-500/20 text-yellow-400'
                  }`}>
                    {device.healthScore}
                  </span>
                  <span className="text-gray-500 truncate text-xs">{device.description.substring(0, 30)}...</span>
                </div>
                
                <button
                  onClick={() => dismissDevice(device.id)}
                  className="p-1 rounded text-gray-500 hover:text-orange-accent transition-all opacity-0 group-hover:opacity-100 flex-shrink-0"
                  title="Dismiss"
                >
                  <X size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Header with View Mode Selector */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <h1 className="text-3xl font-bold text-cyan-accent" 
            style={{ textShadow: '0 0 20px #00e5ff, 0 0 40px #00e5ff' }}>
          Software Changes
        </h1>
        
        <div className="flex items-center gap-4 w-full sm:w-auto">
          {/* View Mode Selector */}
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => setViewMode('table')}
              className={`px-3 py-2 rounded text-sm font-semibold transition-all ${
                viewMode === 'table'
                  ? 'bg-cyan-accent/20 border border-cyan-accent/40 text-cyan-accent'
                  : 'bg-dark-border/40 border border-dark-border/60 text-gray-400 hover:text-cyan-accent'
              }`}
              title="Table View"
            >
              Table
            </button>
            <button
              onClick={() => setViewMode('health-timeline')}
              className={`px-3 py-2 rounded text-sm font-semibold transition-all ${
                viewMode === 'health-timeline'
                  ? 'bg-cyan-accent/20 border border-cyan-accent/40 text-cyan-accent'
                  : 'bg-dark-border/40 border border-dark-border/60 text-gray-400 hover:text-cyan-accent'
              }`}
              title="Health Timeline"
            >
              <Clock size={16} />
            </button>
            <button
              onClick={() => setViewMode('risk-matrix')}
              className={`px-3 py-2 rounded text-sm font-semibold transition-all ${
                viewMode === 'risk-matrix'
                  ? 'bg-cyan-accent/20 border border-cyan-accent/40 text-cyan-accent'
                  : 'bg-dark-border/40 border border-dark-border/60 text-gray-400 hover:text-cyan-accent'
              }`}
              title="Risk Matrix"
            >
              <TrendingUp size={16} />
            </button>
            <button
              onClick={() => setViewMode('health-pulse')}
              className={`px-3 py-2 rounded text-sm font-semibold transition-all ${
                viewMode === 'health-pulse'
                  ? 'bg-cyan-accent/20 border border-cyan-accent/40 text-cyan-accent'
                  : 'bg-dark-border/40 border border-dark-border/60 text-gray-400 hover:text-cyan-accent'
              }`}
              title="Health Pulse"
            >
              <Activity size={16} />
            </button>
            <button
              onClick={() => setViewMode('confidence-sankey')}
              className={`px-3 py-2 rounded text-sm font-semibold transition-all ${
                viewMode === 'confidence-sankey'
                  ? 'bg-cyan-accent/20 border border-cyan-accent/40 text-cyan-accent'
                  : 'bg-dark-border/40 border border-dark-border/60 text-gray-400 hover:text-cyan-accent'
              }`}
              title="Confidence Flow"
            >
              <Target size={16} />
            </button>
            <button
              onClick={() => setViewMode('platform-comparison')}
              className={`px-3 py-2 rounded text-sm font-semibold transition-all ${
                viewMode === 'platform-comparison'
                  ? 'bg-cyan-accent/20 border border-cyan-accent/40 text-cyan-accent'
                  : 'bg-dark-border/40 border border-dark-border/60 text-gray-400 hover:text-cyan-accent'
              }`}
              title="Platform Health"
            >
              <Server size={16} />
            </button>
            <button
              onClick={() => setViewMode('state-swimlane')}
              className={`px-3 py-2 rounded text-sm font-semibold transition-all ${
                viewMode === 'state-swimlane'
                  ? 'bg-cyan-accent/20 border border-cyan-accent/40 text-cyan-accent'
                  : 'bg-dark-border/40 border border-dark-border/60 text-gray-400 hover:text-cyan-accent'
              }`}
              title="State Timeline"
            >
              <GitBranch size={16} />
            </button>
            <button
              onClick={() => setViewMode('entity-sunburst')}
              className={`px-3 py-2 rounded text-sm font-semibold transition-all ${
                viewMode === 'entity-sunburst'
                  ? 'bg-cyan-accent/20 border border-cyan-accent/40 text-cyan-accent'
                  : 'bg-dark-border/40 border border-dark-border/60 text-gray-400 hover:text-cyan-accent'
              }`}
              title="Entity Distribution"
            >
              <Layers size={16} />
            </button>
            <button
              onClick={() => setViewMode('network-graph')}
              className={`px-3 py-2 rounded text-sm font-semibold transition-all ${
                viewMode === 'network-graph'
                  ? 'bg-cyan-accent/20 border border-cyan-accent/40 text-cyan-accent'
                  : 'bg-dark-border/40 border border-dark-border/60 text-gray-400 hover:text-cyan-accent'
              }`}
              title="Dependency Network"
            >
              <Network size={16} />
            </button>
            <button
              onClick={() => setViewMode('violin-plot')}
              className={`px-3 py-2 rounded text-sm font-semibold transition-all ${
                viewMode === 'violin-plot'
                  ? 'bg-cyan-accent/20 border border-cyan-accent/40 text-cyan-accent'
                  : 'bg-dark-border/40 border border-dark-border/60 text-gray-400 hover:text-cyan-accent'
              }`}
              title="Health Distribution"
            >
              <TrendingUp size={16} />
            </button>
            <button
              onClick={() => setViewMode('recovery-chart')}
              className={`px-3 py-2 rounded text-sm font-semibold transition-all ${
                viewMode === 'recovery-chart'
                  ? 'bg-cyan-accent/20 border border-cyan-accent/40 text-cyan-accent'
                  : 'bg-dark-border/40 border border-dark-border/60 text-gray-400 hover:text-cyan-accent'
              }`}
              title="Time to Recovery"
            >
              <Clock size={16} />
            </button>
            <button
              onClick={() => setViewMode('correlation')}
              className={`px-3 py-2 rounded text-sm font-semibold transition-all ${
                viewMode === 'correlation'
                  ? 'bg-cyan-accent/20 border border-cyan-accent/40 text-cyan-accent'
                  : 'bg-dark-border/40 border border-dark-border/60 text-gray-400 hover:text-cyan-accent'
              }`}
              title="Confidence vs Health"
            >
              <Target size={16} />
            </button>
          </div>

          {/* Troubleshoot Button - Admin Only */}
          {isAdmin && (
            <div className="flex flex-col items-center">
              <button
                onClick={startTroubleshoot}
                className="px-5 py-2.5 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-lg font-semibold flex items-center gap-2 shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5"
                style={{ boxShadow: '0 4px 12px rgba(239, 68, 68, 0.4)' }}
              >
                <Wifi size={18} />
                Troubleshoot
              </button>
              <span className="text-xs text-gray-500 mt-1">(Admin Function Only)</span>
            </div>
          )}

          {/* Search Bar */}
          <div className="relative flex-1 sm:w-80">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
            <input
              type="text"
              placeholder="Search changes..."
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
              className="w-full pl-10 pr-4 py-2 bg-dark-card border border-dark-border rounded text-sm text-white placeholder-gray-500 focus:outline-none focus:border-cyan-accent/60 transition-all"
              style={{ boxShadow: '0 0 20px rgba(0, 229, 255, 0.1)' }}
            />
          </div>
        </div>
      </div>

      {/* Conditional View Rendering */}
      {loading ? (
        <div className="bg-dark-card rounded-lg border border-dark-border p-12 text-center">
          <div className="text-cyan-accent text-lg">Loading data...</div>
        </div>
      ) : error ? (
        <div className="bg-dark-card rounded-lg border border-red-500/40 p-12 text-center">
          <div className="text-red-400 mb-2">Error loading data</div>
          <div className="text-sm text-gray-500 mb-4">{error}</div>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-cyan-accent/20 border border-cyan-accent/40 text-cyan-accent rounded hover:bg-cyan-accent/30 transition-all text-sm"
          >
            Retry
          </button>
        </div>
      ) : (
        <>
          {viewMode === 'health-timeline' && <HealthTimelineHeatmap />}
          {viewMode === 'risk-matrix' && <DeploymentRiskMatrix />}
          {viewMode === 'health-pulse' && <HealthPulseDashboard />}
          {viewMode === 'confidence-sankey' && <ConfidenceSankey data={filteredAndSortedData} />}
          {viewMode === 'platform-comparison' && <PlatformHealthComparison data={filteredAndSortedData} />}
          {viewMode === 'state-swimlane' && <StateTransitionSwimlane data={filteredAndSortedData} />}
          {viewMode === 'entity-sunburst' && <EntityTypeSunburst data={filteredAndSortedData} />}
          {viewMode === 'network-graph' && <DependencyNetworkGraph data={filteredAndSortedData} />}
          {viewMode === 'violin-plot' && <HealthScoreViolin data={filteredAndSortedData} />}
          {viewMode === 'recovery-chart' && <TimeToRecoveryChart data={filteredAndSortedData} />}
          {viewMode === 'correlation' && <ConfidenceHealthCorrelation data={filteredAndSortedData} />}
          {viewMode === 'table' && (
            <div className="bg-dark-card rounded-lg border border-dark-border overflow-hidden relative"
                 style={{ 
                   background: '#0d0d0d',
                   boxShadow: '0 0 40px rgba(0, 229, 255, 0.1), inset 0 0 60px rgba(0, 0, 0, 0.5)'
                 }}>
              
              {/* Table */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-dark-border/50">
                      <th className="text-left p-4 text-xs font-semibold text-gray-400 uppercase tracking-wider cursor-pointer hover:text-cyan-accent transition-colors" onClick={() => handleSort('date')}>
                        <div className="flex items-center gap-1">
                          Date (MT)
                          {getSortIcon('date')}
                        </div>
                      </th>
                      <th className="text-left p-4 text-xs font-semibold text-gray-400 uppercase tracking-wider cursor-pointer hover:text-cyan-accent transition-colors" onClick={() => handleSort('entity')}>
                        <div className="flex items-center gap-1">
                          Entity
                          {getSortIcon('entity')}
                        </div>
                      </th>
                      <th className="text-left p-4 text-xs font-semibold text-gray-400 uppercase tracking-wider cursor-pointer hover:text-cyan-accent transition-colors" onClick={() => handleSort('changeNumber')}>
                        <div className="flex items-center gap-1">
                          Change #
                          {getSortIcon('changeNumber')}
                        </div>
                      </th>
                      <th className="text-left p-4 text-xs font-semibold text-gray-400 uppercase tracking-wider cursor-pointer hover:text-cyan-accent transition-colors" onClick={() => handleSort('description')}>
                        <div className="flex items-center gap-1">
                          Description
                          {getSortIcon('description')}
                        </div>
                      </th>
                      <th className="text-left p-4 text-xs font-semibold text-gray-400 uppercase tracking-wider cursor-pointer hover:text-cyan-accent transition-colors" onClick={() => handleSort('state')}>
                        <div className="flex items-center gap-1">
                          State
                          {getSortIcon('state')}
                        </div>
                      </th>
                      <th className="text-center p-4 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                        Health
                      </th>
                      <th className="text-center p-4 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                        Dynatrace
                      </th>
                      <th className="text-left p-4 text-xs font-semibold text-gray-400 uppercase tracking-wider cursor-pointer hover:text-cyan-accent transition-colors" onClick={() => handleSort('dependencies')}>
                        <div className="flex items-center gap-1">
                          Dependencies
                          {getSortIcon('dependencies')}
                        </div>
                      </th>
                      <th className="text-center p-4 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                        Network Map
                      </th>
                      <th className="text-center p-4 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                        Metrics
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedData.length === 0 ? (
                      <tr>
                        <td colSpan={10} className="p-8 text-center text-gray-400">
                          No data found
                        </td>
                      </tr>
                    ) : (
                      paginatedData.map((item, index) => (
                        <tr key={item.id || index} 
                            className="border-b border-dark-border/30 hover:bg-dark-border/20 transition-all group"
                            style={{ background: index % 2 === 0 ? 'rgba(20, 20, 20, 0.3)' : 'rgba(13, 13, 13, 0.3)' }}>
                          {/* Date */}
                          <td className="p-4">
                            <div className="text-sm text-gray-400 font-mono">
                              <div>{item.date}</div>
                              <div className="text-xs text-gray-600">{item.time}</div>
                            </div>
                          </td>
                          
                          {/* Entity Icon */}
                          <td className="p-4">
                            <div className="flex items-center justify-center w-10 h-10 rounded bg-dark-border/40 border border-dark-border/60">
                              {getEntityIcon(item.entity)}
                            </div>
                          </td>
                          
                          {/* Change Number */}
                          <td className="p-4">
                            {item.servicenowLink ? (
                              <a
                                href={item.servicenowLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center space-x-1 px-3 py-1 rounded bg-cyan-accent/20 border border-cyan-accent/40 text-cyan-accent text-sm font-semibold hover:bg-cyan-accent/30 transition-all"
                                style={{ boxShadow: '0 0 15px rgba(0, 229, 255, 0.2)' }}
                              >
                                <span>{item.changeNumber}</span>
                                <ExternalLink size={12} />
                              </a>
                            ) : (
                              <div className="inline-flex items-center space-x-1 px-3 py-1 rounded bg-cyan-accent/20 border border-cyan-accent/40 text-cyan-accent text-sm font-semibold"
                                   style={{ boxShadow: '0 0 15px rgba(0, 229, 255, 0.2)' }}>
                                <span>{item.changeNumber}</span>
                              </div>
                            )}
                          </td>
                          
                          {/* Description */}
                          <td className="p-4">
                            <div className="text-sm text-white font-medium max-w-xs truncate" title={item.description}>
                              {item.description}
                            </div>
                            <div className="text-xs text-gray-500 mt-0.5">{item.author}</div>
                          </td>
                          
                          {/* State */}
                          <td className="p-4">
                            <span className={`text-sm font-semibold ${item.stateColor}`}
                                  style={{ textShadow: '0 0 10px currentColor' }}>
                              {item.state}
                            </span>
                          </td>
                          
                          {/* Health */}
                          <td className="p-4 text-center">
                            <div className="inline-flex items-center justify-center w-8 h-8 rounded bg-dark-border/40 border border-purple-accent/40"
                                 style={{ boxShadow: '0 0 10px rgba(178, 75, 243, 0.2)' }}
                                 title={`${item.health} - Score: ${item.healthScore}`}>
                              {getHealthIcon(item.health)}
                            </div>
                          </td>
                          
                          {/* Dynatrace */}
                          <td className="p-4 text-center">
                            {item.dynatraceLink ? (
                              <a
                                href={item.dynatraceLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center justify-center w-8 h-8 rounded bg-dark-border/40 border border-lime-accent/40 hover:bg-lime-accent/10 transition-all"
                                style={{ boxShadow: '0 0 10px rgba(178, 255, 89, 0.2)' }}
                                title="View in Dynatrace"
                              >
                                <GitBranch size={16} className="text-lime-accent" />
                              </a>
                            ) : (
                              <div className="inline-flex items-center justify-center w-8 h-8 rounded bg-dark-border/40 border border-dark-border/60 opacity-40">
                                <GitBranch size={16} className="text-gray-600" />
                              </div>
                            )}
                          </td>
                          
                          {/* Dependencies */}
                          <td className="p-4">
                            <span className="text-sm text-gray-500">{item.dependencies}</span>
                          </td>
                          
                          {/* Network Map */}
                          <td className="p-4 text-center">
                            <button 
                              onClick={() => {
                                setSelectedItem(item);
                                setShowNetworkModal(true);
                              }}
                              className="inline-flex items-center justify-center w-8 h-8 rounded bg-dark-border/40 border border-orange-accent/40 hover:bg-orange-accent/10 transition-all cursor-pointer"
                              style={{ boxShadow: '0 0 10px rgba(255, 107, 53, 0.2)' }}
                              title="View Network Map">
                              <Network size={16} className="text-orange-accent" />
                            </button>
                          </td>
                          
                          {/* Metrics */}
                          <td className="p-4 text-center">
                            <button 
                              onClick={() => {
                                setSelectedItem(item);
                                setShowDetailsModal(true);
                              }}
                              className="inline-flex items-center justify-center w-8 h-8 rounded bg-dark-border/40 border border-purple-accent/40 hover:bg-purple-accent/10 transition-all cursor-pointer"
                              style={{ boxShadow: '0 0 10px rgba(178, 75, 243, 0.2)' }}
                              title="View Details & Metrics">
                              <Info size={16} className="text-purple-accent" />
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4 border-t border-dark-border/50">
                <div className="flex items-center gap-2 text-sm text-gray-400">
                  <span>Show</span>
                  <select
                    value={itemsPerPage}
                    onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                    className="px-2 py-1 bg-dark-card border border-dark-border rounded text-white focus:outline-none focus:border-cyan-accent/60"
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                  </select>
                  <span>of {filteredAndSortedData.length} entries</span>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                    className="p-2 rounded bg-dark-border/40 border border-dark-border/60 text-gray-400 hover:text-cyan-accent hover:border-cyan-accent/40 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                  >
                    <ChevronsLeft size={16} />
                  </button>
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="p-2 rounded bg-dark-border/40 border border-dark-border/60 text-gray-400 hover:text-cyan-accent hover:border-cyan-accent/40 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                  >
                    <ChevronLeft size={16} />
                  </button>
                  
                  <div className="flex items-center gap-1">
                    {[...Array(totalPages)].map((_, i) => {
                      const page = i + 1;
                      if (totalPages <= 7 || page === 1 || page === totalPages || (page >= currentPage - 1 && page <= currentPage + 1)) {
                        return (
                          <button
                            key={page}
                            onClick={() => setCurrentPage(page)}
                            className={`px-3 py-1 rounded text-sm font-semibold transition-all ${
                              currentPage === page
                                ? 'bg-cyan-accent/20 border border-cyan-accent/40 text-cyan-accent'
                                : 'bg-dark-border/40 border border-dark-border/60 text-gray-400 hover:text-cyan-accent hover:border-cyan-accent/40'
                            }`}
                            style={currentPage === page ? { boxShadow: '0 0 15px rgba(0, 229, 255, 0.2)' } : {}}
                          >
                            {page}
                          </button>
                        );
                      } else if (page === currentPage - 2 || page === currentPage + 2) {
                        return <span key={page} className="text-gray-600">...</span>;
                      }
                      return null;
                    })}
                  </div>

                  <button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    className="p-2 rounded bg-dark-border/40 border border-dark-border/60 text-gray-400 hover:text-cyan-accent hover:border-cyan-accent/40 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                  >
                    <ChevronRight size={16} />
                  </button>
                  <button
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                    className="p-2 rounded bg-dark-border/40 border border-dark-border/60 text-gray-400 hover:text-cyan-accent hover:border-cyan-accent/40 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                  >
                    <ChevronsRight size={16} />
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Modals */}
      {showDetailsModal && selectedItem && (
        <DetailsModal 
          item={selectedItem} 
          onClose={() => {
            setShowDetailsModal(false);
            setSelectedItem(null);
          }} 
        />
      )}

      {showNetworkModal && selectedItem && (
        <NetworkMapModal 
          item={selectedItem} 
          onClose={() => {
            setShowNetworkModal(false);
            setSelectedItem(null);
          }} 
        />
      )}
    </div>
  );
};

export default Software;
